import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoginSchema } from '@connectdevice/types';
import type { LoginPayload } from '@connectdevice/types';
import { useAuth } from '../hooks/useAuth';
import { Button } from '@connectdevice/ui';
import { FlaskConical, Languages, ShieldCheck } from 'lucide-react';
import { useI18n } from '../i18n';
import type { Language } from '../i18n';

export function LoginPage() {
  const { login, isLoading, sessionExpired } = useAuth();
  const { language, languages, languageLabels, setLanguage, t } = useI18n();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<LoginPayload>({
    resolver: zodResolver(LoginSchema),
  });

  const onSubmit = async (data: LoginPayload) => {
    try {
      await login(data.username, data.password);
    } catch {
      setError('root', { message: t('auth.invalidCredentials') });
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-console-base">
        <p className="font-mono text-console-muted">{t('auth.loading')}</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-console-base p-4 text-console-text">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-lg border border-console-blueSoft/40 bg-console-panelHigh text-console-blueSoft">
            <FlaskConical size={28} />
          </div>
          <h1 className="text-4xl font-bold text-console-blueSoft">ConnectDevice</h1>
          <p className="mt-2 text-sm font-bold uppercase tracking-[0.16em] text-console-muted">{t('app.platform')}</p>
        </div>

        <div className="console-card p-8">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-console-text">{t('auth.signIn')}</h2>
              <p className="mt-1 text-sm text-console-muted">{t('app.localEnvironment')}</p>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 rounded border border-console-border bg-console-panelHigh px-2 py-1.5 text-console-muted">
                <Languages size={16} />
                <span className="sr-only">{t('language.label')}</span>
                <select
                  aria-label={t('language.label')}
                  className="bg-transparent text-xs font-bold uppercase text-console-text outline-none"
                  value={language}
                  onChange={(event) => setLanguage(event.target.value as Language)}
                >
                  {languages.map((option) => (
                    <option key={option} value={option}>
                      {t(languageLabels[option])}
                    </option>
                  ))}
                </select>
              </label>
              <ShieldCheck className="text-console-good" size={24} />
            </div>
          </div>

          {sessionExpired && (
            <p className="mb-4 rounded border border-console-half/40 bg-console-half/10 px-4 py-3 text-sm text-console-half">
              {t('auth.sessionExpired')}
            </p>
          )}

          <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-4">
            <div>
              <label className="console-label mb-2 block">
                {t('auth.username')}
              </label>
              <input
                type="text"
                autoComplete="username"
                className="w-full rounded border border-console-border bg-console-base px-3 py-3 text-sm text-console-text outline-none focus:ring-2 focus:ring-console-blueSoft/40"
                {...register('username')}
              />
              {errors.username && (
                <p className="mt-1 text-xs text-console-defect">{t('auth.validation.usernameRequired')}</p>
              )}
            </div>

            <div>
              <label className="console-label mb-2 block">
                {t('auth.password')}
              </label>
              <input
                type="password"
                autoComplete="current-password"
                className="w-full rounded border border-console-border bg-console-base px-3 py-3 text-sm text-console-text outline-none focus:ring-2 focus:ring-console-blueSoft/40"
                {...register('password')}
              />
              {errors.password && (
                <p className="mt-1 text-xs text-console-defect">{t('auth.validation.passwordRequired')}</p>
              )}
            </div>

            {errors.root && (
              <p className="text-center text-sm text-console-defect">{errors.root.message}</p>
            )}

            <Button type="submit" className="console-button console-button-primary w-full" disabled={isSubmitting}>
              {isSubmitting ? t('auth.signingIn') : t('auth.signIn')}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
